import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FGM } from "../brand";
import { getActiveReferral, shareUrlFor } from "../referral";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    location: "",
    social_handle: "",
    sport: "",
    why_fgm: "",
    payout_method: "venmo",
    payout_handle: "",
};

export default function Ambassador() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [myCode, setMyCode] = useState(null);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (form.why_fgm.trim().length < 10) {
            toast.error("Tell us a bit more — at least 10 characters.");
            return;
        }
        setLoading(true);
        try {
            const payload = { ...form, referral_code: getActiveReferral() || undefined };
            const res = await axios.post(`${API}/ambassador`, payload);
            setMyCode(res.data?.my_referral_code || null);
            setDone(true);
            setForm(initial);
            toast.success("Application received. Welcome to the movement.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
            toast.error(Array.isArray(msg) ? msg[0]?.msg || "Invalid input" : msg);
        } finally {
            setLoading(false);
        }
    };

    const copyShare = async () => {
        const url = shareUrlFor(myCode);
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Share link copied.");
        } catch {
            toast.error("Copy failed — long-press to copy manually.");
        }
    };

    return (
        <section
            id="ambassador"
            data-testid="ambassador-section"
            className="relative bg-fgm-dark py-16 md:py-24 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                {/* Left — Visual */}
                <div className="lg:col-span-5 relative">
                    <div className="sticky top-28">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="h-px w-12 bg-[#E63946]" />
                            <span className="font-display tracking-[0.35em] text-zinc-400 text-sm uppercase">
                                / 04 — Brand Ambassador
                            </span>
                        </div>

                        {/* Hero flyer — primary visual */}
                        <div className="relative aspect-[4/5] overflow-hidden bg-black mb-6">
                            <img
                                src={FGM.ambassadorFlyer}
                                alt="FGM Brand Ambassador — Ready to Represent More Than a Brand?"
                                className="w-full h-full object-cover"
                             loading="lazy" decoding="async" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6">
                                <div className="flex gap-6 text-xs font-display tracking-[0.25em] text-zinc-300 uppercase">
                                    <div className="flex items-center gap-2"><span className="text-[#E63946]">●</span> Focus</div>
                                    <div className="flex items-center gap-2"><span className="text-white">●</span> Grind</div>
                                    <div className="flex items-center gap-2"><span className="text-[#1D4ED8]">●</span> Motivate</div>
                                </div>
                            </div>
                        </div>

                        <div className="fgm-stripe-divider w-20 mb-4" />
                        <p className="text-zinc-400 font-body leading-relaxed max-w-md">
                            Join the FGM Apparel movement. We're looking for
                            motivated individuals who live the mindset —
                            <span className="text-white"> Focus. Grind. Motivate.</span>
                        </p>
                    </div>
                </div>

                {/* Right — Form */}
                <div className="lg:col-span-7">
                    <div className="bg-fgm-surface border border-white/10 p-6 md:p-10 relative">
                        <div className="absolute top-0 left-0 fgm-stripe-divider w-24" />

                        {done ? (
                            <div
                                data-testid="ambassador-success"
                                className="py-16 text-center"
                            >
                                <div className="font-display text-7xl text-[#E63946] mb-4">✓</div>
                                <h3 className="font-display uppercase text-3xl md:text-4xl text-white mb-3">
                                    Welcome To The Movement
                                </h3>
                                <p className="text-zinc-400 max-w-md mx-auto mb-6">
                                    Your application is in. Our team reviews every submission —
                                    if it's a match, we'll be in touch via email.
                                </p>

                                {myCode && (
                                    <div
                                        data-testid="ambassador-share-block"
                                        className="bg-black/40 border border-white/10 p-5 max-w-lg mx-auto mb-8 text-left"
                                    >
                                        <div className="fgm-stripe-divider w-12 mb-3" />
                                        <div className="font-display text-xl text-white uppercase mb-1">
                                            Your Share Link
                                        </div>
                                        <p className="text-xs text-zinc-400 mb-3">
                                            Share this link. Every signup or order through it
                                            is credited to you on the ambassador leaderboard.
                                        </p>
                                        <div className="flex items-center gap-2 bg-black border border-white/10 p-3">
                                            <input
                                                data-testid="ambassador-share-link"
                                                readOnly
                                                value={shareUrlFor(myCode)}
                                                className="flex-1 bg-transparent text-zinc-300 text-sm font-mono outline-none truncate"
                                                onFocus={(e) => e.target.select()}
                                            />
                                            <button
                                                data-testid="ambassador-share-copy"
                                                type="button"
                                                onClick={copyShare}
                                                className="text-xs font-display tracking-[0.25em] uppercase text-white hover:text-[#E63946] shrink-0"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <button
                                    data-testid="ambassador-submit-again"
                                    onClick={() => {
                                        setDone(false);
                                        setMyCode(null);
                                    }}
                                    className="btn-outline"
                                >
                                    Submit Another
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="ambassador-form" className="space-y-6">
                                <div>
                                    <h3 className="font-display uppercase text-3xl md:text-4xl text-white mb-1">
                                        Apply Now
                                    </h3>
                                    <p className="text-zinc-400 text-sm">
                                        Help inspire the culture through fitness, lifestyle, leadership, and ambition.
                                    </p>
                                    <p className="text-zinc-500 text-xs mt-2">
                                        Already an ambassador?{" "}
                                        <a href="/portal" data-testid="ambassador-portal-link" className="text-[#E63946] hover:text-white underline">
                                            Sign in to your portal →
                                        </a>
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="fgm-label">Full Name</label>
                                        <input
                                            data-testid="ambassador-input-name"
                                            type="text"
                                            required
                                            value={form.name}
                                            onChange={update("name")}
                                            className="fgm-input"
                                            placeholder="Your name"
                                        />
                                    </div>
                                    <div>
                                        <label className="fgm-label">Email</label>
                                        <input
                                            data-testid="ambassador-input-email"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={update("email")}
                                            className="fgm-input"
                                            placeholder="you@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="fgm-label">Location</label>
                                        <input
                                            data-testid="ambassador-input-location"
                                            type="text"
                                            required
                                            value={form.location}
                                            onChange={update("location")}
                                            className="fgm-input"
                                            placeholder="City, Country"
                                        />
                                    </div>
                                    <div>
                                        <label className="fgm-label">Social Handle</label>
                                        <input
                                            data-testid="ambassador-input-social"
                                            type="text"
                                            required
                                            value={form.social_handle}
                                            onChange={update("social_handle")}
                                            className="fgm-input"
                                            placeholder="@yourhandle"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="fgm-label">Sport / Discipline</label>
                                        <input
                                            data-testid="ambassador-input-sport"
                                            type="text"
                                            required
                                            value={form.sport}
                                            onChange={update("sport")}
                                            className="fgm-input"
                                            placeholder="Bodybuilding, Running, Cycling, Creator..."
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="fgm-label">Why FGM?</label>
                                        <textarea
                                            data-testid="ambassador-input-why"
                                            required
                                            value={form.why_fgm}
                                            onChange={update("why_fgm")}
                                            className="fgm-input"
                                            rows={5}
                                            placeholder="Tell us what Focus. Grind. Motivate. means to you."
                                        />
                                    </div>

                                    {/* Commission payout info — used to pay 10% commission monthly */}
                                    <div className="md:col-span-2 pt-2 border-t border-white/5">
                                        <p className="text-[10px] font-display tracking-[0.3em] uppercase text-[#E63946] mb-3">
                                            💰 Commission Payout (10% on every sale)
                                        </p>
                                    </div>
                                    <div>
                                        <label className="fgm-label">Payout Method</label>
                                        <select
                                            data-testid="ambassador-input-payout-method"
                                            value={form.payout_method}
                                            onChange={update("payout_method")}
                                            className="fgm-input"
                                        >
                                            <option value="venmo">Venmo</option>
                                            <option value="paypal">PayPal</option>
                                            <option value="zelle">Zelle</option>
                                            <option value="cashapp">Cash App</option>
                                            <option value="store_credit">FGM store credit only</option>
                                            <option value="other">Other (we'll DM you)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="fgm-label">
                                            Payout Handle{" "}
                                            <span className="text-zinc-500 normal-case tracking-normal text-[10px]">
                                                (@username, email, or phone)
                                            </span>
                                        </label>
                                        <input
                                            data-testid="ambassador-input-payout-handle"
                                            value={form.payout_handle}
                                            onChange={update("payout_handle")}
                                            className="fgm-input"
                                            placeholder={
                                                form.payout_method === "venmo" ? "@your-venmo" :
                                                form.payout_method === "paypal" ? "you@email.com" :
                                                form.payout_method === "zelle" ? "email or phone" :
                                                form.payout_method === "cashapp" ? "$cashtag" :
                                                form.payout_method === "store_credit" ? "(leave blank)" :
                                                "we'll follow up"
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                                    <button
                                        data-testid="ambassador-submit"
                                        type="submit"
                                        disabled={loading}
                                        className="btn-primary disabled:opacity-50"
                                    >
                                        {loading ? "Submitting…" : "Submit Application"}
                                    </button>
                                    <div className="text-xs font-display tracking-[0.25em] text-zinc-500 uppercase">
                                        We Review Every Single Application
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
